import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import { saloonFonts, saloonTokens } from 'styles/saloonTheme';

import type { ActiveTable } from 'components/SaloonLobby/types';
import YourTables from 'components/SaloonLobby/YourTables';
import { parseWaitlistStartMs } from 'components/Waitlist/useWaitlistCountdown';

import { isSwitcherExpanded, setSwitcherExpanded } from './tableSwitcherOpen';

// ---------------------------------------------------------------------------
// TableSwitcher — a compact, centered "table-count" badge that toggles a row of
// the player's open-table previews.
//
//   Collapsed:                 Expanded (previews appear directly below):
//     [stacked card backs]       [stacked card backs]
//            3                          3
//         TABLES                     TABLES
//                                [fan] [active fan] [fan]
//
// It is NOT a dropdown panel — the expanded previews float on the felt directly
// below the badge with no panel chrome. Count is dynamic, and the previews are
// the SAME card fans the lobby renders (<YourTables bare>): Omaha 4-card /
// Hold'em 2-card fans, the active-table ring, turn pulse, and waitlist (#N /
// WAITLIST) stamps. Switching reuses the existing onOpenTable handler. The
// Add Chips "+" deliberately lives elsewhere (beside the balance), never here.
// ---------------------------------------------------------------------------

interface TableSwitcherProps {
  // Open tables, in display order (from useActiveTables — the same list the fan
  // and nav chips use).
  tables: ActiveTable[];
  // gameID of the table currently being viewed — its fan gets the selection
  // contour.
  activeGameID?: string;
  // Switch to a table. Same signature as the fan / nav-chip handlers.
  onOpenTable: (gameID: string, game: string) => boolean | void;
  // Open another table (returns to the lobby). Withheld by the caller at the
  // open-table cap, exactly as it is for the unstacked strip — so the "+"
  // disappears when there is no slot left.
  onAddTable?: () => void;
}

// The tappable badge IS a card (CSS card back, no asset) with the open-table
// count + label printed on its face. Behind it sit faint cards — one per OTHER
// open table — so the stack depth reflects how many tables are open. Decorative
// chrome is hidden from a11y; the count/label stay readable to AT.
const MAX_BADGE_BEHIND = 3; // cap the fanned stack (open tables are capped low)
const CardCountBadge: React.FC<{
  count: number;
  label: string;
  actSeconds?: number | null;
  actLabel?: string;
  expanded?: boolean;
}> = ({ count, label, actSeconds, actLabel = 'ACT', expanded = false }) => {
  const behind = Math.max(0, Math.min(count - 1, MAX_BADGE_BEHIND));
  // When an action deadline is imminent the front card shows the nearest
  // countdown + a call-to-action pill (ACT for a turn, JOIN for a waitlist
  // seat-ready window) instead of the table count.
  const acting = typeof actSeconds === 'number';
  // Lift + grow the card while the previews are expanded; eases back on collapse
  // (a simple open/closed state cue). Transition handles both directions.
  const stackStyle: CSSProperties = {
    ...cardStackStyle,
    transform: expanded ? 'translateY(-2px) scale(1.07)' : 'none',
    transition: 'transform 0.18s ease'
  };
  return (
    <span style={stackStyle} data-testid='count-card-stack'>
      {Array.from({ length: behind }, (_, i) => {
        const depth = behind - i; // 1 = closest behind the front, larger = farther
        return (
          <span
            key={i}
            aria-hidden
            style={{
              ...cardBehindStyle,
              transform: `rotate(${-5 * depth}deg) translateX(${-3 * depth}px)`,
              opacity: Math.max(0.4, 0.75 - 0.12 * (depth - 1))
            }}
          />
        );
      })}
      {/* Green alert ring hugging the front card (border-only, so the card and
          number show through). The pill below overlaps its bottom edge. */}
      {acting && <span style={actRingStyle} aria-hidden />}
      <span style={cardFrontStyle}>
        {acting ? (
          <span style={actCountStyle}>{actSeconds}</span>
        ) : (
          <>
            <span style={countStyle}>{count}</span>
            <span style={labelStyle}>{label}</span>
          </>
        )}
      </span>
      {/* ACT / JOIN pill, sitting on the card's bottom edge (through the ring).
          A multi-run vote passes an empty label and so shows no pill (AC-8.3);
          the ring and the countdown digit above are the whole treatment. */}
      {acting && actLabel && <span style={actPillStyle}>{actLabel}</span>}
    </span>
  );
};

const COLLAPSE_MS = 190;
// A TURN clock alerts on the collapsed badge only within this many seconds of
// its deadline (matches the per-fan shake window). Waitlist seat-ready windows
// alert for their whole countdown instead — see useNearestActCountdown.
const NEAREST_ACT_FROM_SECONDS = 5;

// The pill's word for each kind of pending deadline — empty for a kind that
// gets no word at all. EN-663 AC-8.3 allows a multi-run vote the countdown digit
// inside the dock tile and nothing else: "No dock tile badge for multi-run —
// nothing like the ALL-IN / WINNER badges." So a vote keeps its place in the
// priority order below, its ring and its number, and loses only the word.
const ALERT_LABEL: Record<'act' | 'join' | 'vote', string> = {
  act: 'ACT',
  join: 'JOIN',
  vote: ''
};

// Aggregate the soonest "you must act" deadline across every open table's turn
// clock, its multi-run vote window, AND any seat-ready waitlist accept window,
// ticking live. Turn clocks and vote windows arrive as (total, elapsed)
// snapshots, so they're anchored to wall-clock on change; waitlist windows are
// absolute (start + timeout). Returns the nearest remaining whole seconds, or
// null when nothing is pending.
const useNearestActCountdown = (
  turnClocks: { id: string; total: number; elapsed: number; kind: 'act' | 'vote' }[],
  waitlistDeadlines: number[]
): { seconds: number; kind: 'act' | 'join' | 'vote' } | null => {
  const anchorsRef = useRef<Map<string, { sig: string; deadline: number; kind: 'act' | 'vote' }>>(
    new Map()
  );
  const [, force] = useState(0);

  // Reconcile anchored turn deadlines with the current clocks (re-anchor when a
  // clock's snapshot changes; drop clocks that are gone).
  const now = Date.now();
  const anchors = anchorsRef.current;
  // Keyed by KIND + id: one table can in principle carry a turn clock and a
  // vote window, and a single key would let one silently overwrite the other's
  // anchor.
  const liveIds = new Set(turnClocks.map((c) => `${c.kind}:${c.id}`));
  Array.from(anchors.keys()).forEach((k) => {
    if (!liveIds.has(k)) anchors.delete(k);
  });
  turnClocks.forEach((c) => {
    const key = `${c.kind}:${c.id}`;
    const sig = `${c.total}|${c.elapsed}`;
    const existing = anchors.get(key);
    if (!existing || existing.sig !== sig) {
      anchors.set(key, {
        sig,
        deadline: now + Math.max(0, c.total - c.elapsed) * 1000,
        kind: c.kind
      });
    }
  });

  const hasCandidates = anchors.size > 0 || waitlistDeadlines.length > 0;
  useEffect(() => {
    if (!hasCandidates) return undefined;
    const id = setInterval(() => force((x) => (x + 1) % 1_000_000), 250);
    return () => clearInterval(id);
  }, [hasCandidates]);

  if (!hasCandidates) return null;
  // Pick the soonest *qualifying* deadline. Turn clocks only qualify in their
  // final seconds (ACT); waitlist seat-ready windows qualify for their WHOLE
  // countdown (JOIN), so the alert fires the moment the seat is offered. Vote
  // windows qualify for their whole countdown too — see below.
  const nowEnd = Date.now();
  let bestDeadline = Infinity;
  let best: { seconds: number; kind: 'act' | 'join' | 'vote' } | null = null;
  // A pending VOTE outranks everything else outright rather than competing on
  // nearest-deadline. The window is 8-15s, the chips are already in, and a
  // missed vote cannot be retried — where a missed turn folds a hand and a
  // missed waitlist seat comes round again. Between two votes the soonest still
  // wins. (EN-10237)
  let bestVoteDeadline = Infinity;
  let bestVote: { seconds: number; kind: 'vote' } | null = null;
  anchors.forEach((a) => {
    const secs = Math.max(0, Math.ceil((a.deadline - nowEnd) / 1000));
    if (secs < 1) return;
    if (a.kind === 'vote') {
      if (a.deadline < bestVoteDeadline) {
        bestVoteDeadline = a.deadline;
        bestVote = { seconds: secs, kind: 'vote' };
      }
      return;
    }
    if (secs <= NEAREST_ACT_FROM_SECONDS && a.deadline < bestDeadline) {
      bestDeadline = a.deadline;
      best = { seconds: secs, kind: 'act' };
    }
  });
  if (bestVote) return bestVote;
  waitlistDeadlines.forEach((d) => {
    const secs = Math.max(0, Math.ceil((d - nowEnd) / 1000));
    if (secs >= 1 && d < bestDeadline) {
      bestDeadline = d;
      best = { seconds: secs, kind: 'join' };
    }
  });
  return best;
};

const TableSwitcher: React.FC<TableSwitcherProps> = ({
  tables,
  activeGameID,
  onOpenTable,
  onAddTable
}) => {
  // Seeded from (and written back to) module state: this header is part of the
  // per-table subtree, so switching tables REMOUNTS it — a plain useState would
  // collapse the previews on every switch, which is precisely what the player
  // opened them to do. See tableSwitcherOpen.
  const [open, setOpenState] = useState<boolean>(isSwitcherExpanded);
  // Did the player expand it during THIS mount? A switcher that mounts already
  // expanded (because they left it open and switched tables, which remounts the
  // header) must not replay the staggered fan-in entrance — that replay is what
  // read as the previews closing and re-opening on every switch.
  const expandedHere = useRef(false);
  const setOpen = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
    setOpenState((prev: boolean) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (value) expandedHere.current = true;
      setSwitcherExpanded(value);
      return value;
    });
  }, []);
  // Keep the previews mounted through the collapse animation, then unmount.
  const [mounted, setMounted] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  // Tables OTHER than the one on screen. Not what the dropdown shows any more —
  // it shows every open table, the current one included and ringed, so the
  // stacked view reads the same as the unstacked strip — but still what the
  // ACT / JOIN alert aggregates: the current table's own clock is already
  // visible on the felt, so only the others need surfacing on the badge.
  const otherTables = activeGameID
    ? tables.filter((table) => String(table.gameID) !== String(activeGameID))
    : tables;

  // Expand → mount immediately (the cards stagger in via `animateIn`).
  // Collapse → play a fold-back animation on the preview row, then unmount. The
  // Web Animations API isn't in jsdom, so tests just unmount synchronously.
  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    if (!mounted) return undefined;
    const el = previewRef.current as any;
    if (!el || typeof el.animate !== 'function') {
      setMounted(false);
      return undefined;
    }
    const anim = el.animate(
      [
        { opacity: 1, transform: 'translateX(-50%) translateY(0) scale(1)' },
        { opacity: 0, transform: 'translateX(-50%) translateY(-6px) scale(0.85)' }
      ],
      { duration: COLLAPSE_MS, easing: 'ease-in', fill: 'forwards' }
    );
    const done = () => setMounted(false);
    anim.onfinish = done;
    anim.oncancel = done;
    return () => anim.cancel();
  }, [open, mounted]);

  // Close on outside click and on Escape (returning focus to the badge so the
  // control stays keyboard-navigable).
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // Aggregate the soonest turn/waitlist deadline so the COLLAPSED badge can warn
  // the player to act (shake + countdown + ACT) even while the previews are hidden.
  const waitlistSpots = useAppSelector((s: any) => s.waitlist?.mySpotByGameId) as
    | Record<string, any>
    | undefined;
  const turnClocks: { id: string; total: number; elapsed: number; kind: 'act' | 'vote' }[] = [];
  otherTables.forEach((t) => {
    if (t.turnClock) {
      turnClocks.push({
        id: t.gameID,
        total: t.turnClock.totalSeconds,
        elapsed: t.turnClock.elapsedSeconds,
        kind: 'act'
      });
    }
    // A multi-run vote pending on a table the player is not looking at — the
    // reason EN-10237 exists. Same shape, same anchoring, different urgency
    // rule (see useNearestActCountdown).
    if (t.voteClock) {
      turnClocks.push({
        id: t.gameID,
        total: t.voteClock.totalSeconds,
        elapsed: t.voteClock.elapsedSeconds,
        kind: 'vote'
      });
    }
  });
  const waitlistDeadlines: number[] = [];
  Object.values(waitlistSpots || {}).forEach((e: any) => {
    if (e && e.yourTurn && e.availableSeats > 0 && e.waitForAcceptStartTime) {
      const deadline =
        parseWaitlistStartMs(e.waitForAcceptStartTime) + Number(e.waitlistTimeOut) * 1000;
      if (Number.isFinite(deadline)) waitlistDeadlines.push(deadline);
    }
  });
  const nearestAct = useNearestActCountdown(turnClocks, waitlistDeadlines);
  // Alert on the collapsed badge only — the previews already handle this when
  // open. Qualification (turn = final seconds, waitlist = whole window) is
  // decided inside the hook, so a non-null result means "alert now".
  const showAct = !open && nearestAct !== null;

  // Shake the collapsed badge while an action deadline is imminent.
  useEffect(() => {
    const el = triggerRef.current as any;
    if (!showAct || !el || typeof el.animate !== 'function') return undefined;
    const anim = el.animate(
      [
        { transform: 'translateX(0) rotate(0deg)' },
        { transform: 'translateX(-1.6px) rotate(-1.6deg)', offset: 0.2 },
        { transform: 'translateX(1.6px) rotate(1.4deg)', offset: 0.4 },
        { transform: 'translateX(-1.1px) rotate(-1deg)', offset: 0.6 },
        { transform: 'translateX(1.1px) rotate(1deg)', offset: 0.8 },
        { transform: 'translateX(0) rotate(0deg)' }
      ],
      { duration: 420, iterations: Infinity, easing: 'ease-in-out' }
    );
    return () => anim.cancel();
  }, [showAct]);

  // Hide the switcher when there's nothing useful to switch TO — no OTHER open
  // table (the player is already on the only one) — unless an imminent ACT /
  // JOIN deadline still needs to surface on the collapsed badge. The COUNT,
  // though, reflects ALL open tables including the current one, so a visible
  // badge reads "2 TABLES" (1 other + the current) rather than "1".
  //
  // Hiding the BADGE must not hide the "+": it is the only way to open a second
  // table from the table view, and TableHeader still renders the band this sits
  // in — so a bare null left stacked players with one table an empty band and
  // no add control (EN-10780). It draws what the unstacked strip draws: the "+"
  // beside the one table, ringed as active, so the player still sees their
  // table's slot and status. Not a queued waitlist card — the collapsed badge
  // never shows one.
  //
  // Adding a table navigates away to the lobby, so collapse the previews first —
  // otherwise they'd be left hanging open over the transition. Clear the module
  // flag directly rather than through setOpen's updater: the navigation can
  // unmount this header before that updater runs, and a flag left `true` fans the
  // previews out on the next table opened. The lone "+" shares this handler, since
  // the player may have expanded the badge before their other table closed.
  const handleAdd = () => {
    setSwitcherExpanded(false);
    setOpenState(false);
    onAddTable?.();
  };

  const nothingToSwitchTo = otherTables.length === 0 || tables.length === 1;
  if (nothingToSwitchTo && nearestAct === null) {
    if (!onAddTable) return null;
    return (
      <div style={rootStyle}>
        <YourTables
          tables={tables}
          onOpenTable={onOpenTable}
          activeGameID={activeGameID}
          onAddTable={handleAdd}
          showWaitlist={false}
          bare
        />
      </div>
    );
  }

  const count = tables.length;
  const label = count === 1 ? 'TABLE' : 'TABLES';

  // Selecting a table (a fan tap) switches to it and collapses the previews.
  // Switching does NOT collapse the previews. A player who expanded them is
  // working across their tables — closing on every tap made them re-open it for
  // each switch. Collapsing stays manual: the badge, an outside tap, or Escape.
  const handleSelect = (gameID: string, game: string) => onOpenTable(gameID, game);

  return (
    <div ref={rootRef} style={rootStyle}>
      <button
        ref={triggerRef}
        type='button'
        onClick={() => setOpen((v) => !v)}
        style={badgeStyle}
        aria-label='Switch tables'
        aria-expanded={open}>
        <CardCountBadge
          count={count}
          label={label}
          actSeconds={showAct && nearestAct ? nearestAct.seconds : null}
          actLabel={ALERT_LABEL[nearestAct?.kind ?? 'act']}
          expanded={open}
        />
      </button>

      {mounted && (
        <div ref={previewRef} style={previewRowStyle} aria-label='Open tables'>
          {/* Reuse the lobby's card-fan previews — Omaha (4-card) / Hold'em
              (2-card) fans, the active-table ring, turn pulse, and waitlist
              stamps. `bare` drops the lobby chrome so only the fans float on the
              felt (no panel).

              EVERY open table is shown, including the one being viewed, which
              carries the same selection dot it does in the unstacked strip — the
              dropdown is the player's map of their tables, and a map missing
              "you are here" is harder to read, not tidier. The trailing "+"
              appears on the same terms as the strip's: the caller withholds
              onAddTable at the open-table cap. */}
          <YourTables
            tables={tables}
            onOpenTable={handleSelect}
            activeGameID={activeGameID}
            onAddTable={onAddTable ? handleAdd : undefined}
            bare
            // Only when the player just expanded it — see expandedHere.
            animateIn={open && expandedHere.current}
          />
        </div>
      )}
    </div>
  );
};

// --- styles (inline, matching the Saloon header aesthetic) -----------------

const rootStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  // The centered band is pass-through; the control re-enables pointer events.
  pointerEvents: 'auto'
};

const badgeStyle: CSSProperties = {
  display: 'flex',
  // Center the tappable card within the header band.
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
  background: 'transparent',
  border: 'none',
  color: saloonTokens.bone,
  cursor: 'pointer',
  fontFamily: 'inherit',
  lineHeight: 1
};

// The card stack — a front card (with the count/label on its face) and a faint
// card peeking out behind it. Normal playing-card proportions, sized to sit
// comfortably in the header without dominating it.
const CARD_W = 36;
const CARD_H = 50;

const cardStackStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: CARD_W,
  height: CARD_H
};

const cardBaseStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: CARD_W,
  height: CARD_H,
  borderRadius: 7,
  // The game's brown leather card back — identical to the face-down cards in
  // the Your-Tables fans (.cardBack / .cardEmpty: rust gradient + near-black
  // edge), so the count badge reads as a real card from the same deck.
  background: `linear-gradient(135deg, ${saloonTokens.rust700}, ${saloonTokens.rust800})`,
  border: '1px solid #1a0a02'
};

const cardBehindStyle: CSSProperties = {
  ...cardBaseStyle,
  transform: 'rotate(-8deg) translateX(-4px)',
  transformOrigin: 'bottom center',
  opacity: 0.7,
  boxShadow: '0 2px 4px rgba(0,0,0,0.4)'
};

const cardFrontStyle: CSSProperties = {
  ...cardBaseStyle,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  boxShadow: '0 2px 6px rgba(0,0,0,0.45)'
};

const countStyle: CSSProperties = {
  fontFamily: saloonFonts.body,
  fontSize: '1.1rem',
  fontWeight: 800,
  letterSpacing: '0.02em',
  color: saloonTokens.bone
};

const labelStyle: CSSProperties = {
  fontFamily: saloonFonts.body,
  fontSize: '0.46rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  color: saloonTokens.orange300
};

// Countdown number when alerting — sized to match the resting table count so the
// alerting badge doesn't balloon larger than the normal "N TABLES" badge.
const actCountStyle: CSSProperties = {
  fontFamily: saloonFonts.body,
  fontSize: '1.1rem',
  fontWeight: 800,
  letterSpacing: '0.02em',
  color: saloonTokens.bone
};

// Green alert ring hugging the front card (border-only, glowing). The pill
// overlaps its bottom edge, matching the in-fan turn-ring + ACT stamp look.
const actRingStyle: CSSProperties = {
  position: 'absolute',
  top: -3,
  left: -3,
  width: CARD_W + 6,
  height: CARD_H + 6,
  borderRadius: 9,
  border: '2px solid rgba(120, 230, 150, 0.95)',
  boxShadow: '0 0 11px rgba(60, 220, 120, 0.7)',
  pointerEvents: 'none',
  zIndex: 2
};

// ACT / JOIN pill — green, white text, sitting on the card's bottom edge so it
// breaks through the ring (matches the fan's ACT stamp). Wider + larger label.
const actPillStyle: CSSProperties = {
  position: 'absolute',
  bottom: -8,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 3,
  padding: '1px 11px',
  borderRadius: 4,
  background: 'linear-gradient(180deg, #5dba6e, #2f8d44)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  color: '#fff',
  fontFamily: saloonFonts.numeric,
  fontSize: '0.62rem',
  fontWeight: 800,
  letterSpacing: '0.16em',
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
};

// Compact preview row floating just below the badge, centered, with only enough
// width to lay the fans out (BareFans scales to fit). A translucent panel sits
// behind the fans to lift them off the felt.
const previewRowStyle: CSSProperties = {
  position: 'absolute',
  // Tucked 6px higher (was +4px) so the row sits closer under the badge.
  top: 'calc(100% + 4px)',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'min(86vw, 340px)',
  display: 'flex',
  justifyContent: 'center',
  // 50%-opacity backing panel so the previews read clearly against the felt.
  borderRadius: 10,
  // Above the felt/meta but only as wide as its content, so it doesn't blanket
  // unrelated controls.
  zIndex: 5,
  pointerEvents: 'auto'
};

export default TableSwitcher;
